/**
 * DOM/lifecycle boundary for Studio's central canvas.
 *
 * Patch 3 deliberately keeps the existing tree renderer. The controller
 * isolates viewport state, node-to-element lookup and disposable runtime
 * sessions now, so the tree can later be replaced by a persistent Plain
 * canvas without teaching the application shell about renderer details.
 */
/** Owns at most one renderer/runtime session and disposes replacements exactly once. */
export class StudioRuntimeSession {
    #current = null;
    get current() {
        return this.#current;
    }
    replace(next) {
        if (next === this.#current)
            return this.#current;
        const previous = this.#current;
        this.#current = next;
        previous?.dispose();
        return this.#current;
    }
    dispose() {
        this.replace(null);
    }
}
/** Stable node-ID lookup owned by the canvas rather than ad-hoc document queries. */
export class StudioElementRegistry {
    #byNodeId = new Map();
    /**
     * First element wins. A node appears twice — once in the outline rail, once as a row in the
     * live canvas — and the outline comes first in document order. That is the one that must take
     * focus after a command, because it is the surface carrying the pick-up/arrow-move handlers.
     */
    refresh(root) {
        this.#byNodeId.clear();
        root.querySelectorAll("[data-node]").forEach((element) => {
            const nodeId = element.dataset.node;
            if (nodeId && !this.#byNodeId.has(nodeId))
                this.#byNodeId.set(nodeId, element);
        });
    }
    get(nodeId) {
        return this.#byNodeId.get(nodeId) ?? null;
    }
    clear() {
        this.#byNodeId.clear();
    }
}
/**
 * Boundary used by mountStudio around the existing full-shell render.
 * Today it preserves the tree canvas viewport and indexes its nodes. In the
 * Plain-canvas migration this class becomes the owner of the persistent mount.
 */
export class StudioCanvasController {
    elements = new StudioElementRegistry();
    #viewport = { scrollLeft: 0, scrollTop: 0 };
    #canvas = null;
    capture() {
        if (!this.#canvas)
            return;
        this.#viewport = {
            scrollLeft: this.#canvas.scrollLeft,
            scrollTop: this.#canvas.scrollTop,
        };
    }
    connect(canvas) {
        this.#canvas = canvas;
        this.elements.clear();
        if (!canvas)
            return;
        this.elements.refresh(canvas);
        canvas.scrollLeft = this.#viewport.scrollLeft;
        canvas.scrollTop = this.#viewport.scrollTop;
    }
    elementForNode(nodeId) {
        return this.elements.get(nodeId);
    }
    dispose() {
        this.#canvas = null;
        this.elements.clear();
        this.#viewport = { scrollLeft: 0, scrollTop: 0 };
    }
}
